import { DetailsPrices, Offer, OfferType, Recurrence } from '@app/api';
import { useMemo } from 'react';
import { applyDurationToPrice } from 'lib/priceComputation';

export enum DisplayablePriceUnit {
  EUR = 'EUR',
  PER_PARTICIPANT = 'PER_PARTICIPANT',
  PER_PARTICIPANT_PER_HOUR = 'PER_PARTICIPANT_PER_HOUR',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export type DisplayablePrice =
  | {
      unit: DisplayablePriceUnit;
      price: number;
      extraCredits?: number;
    }
  | {
      label: 'CUSTOM' | 'INCLUDED' | 'FREE' | 'YOUR_AMOUNT';
      extraCredits?: number;
    };

export const useDisplayablePriceList = (
  prices?: DetailsPrices,
  offer?: Partial<Offer>,
  duration?: number,
  recurrence?: Recurrence,
  credits?: number,
): DisplayablePrice[] => {
  if (!prices) {
    return [];
  }
  return useMemo(
    () => getDisplayablePriceList(prices, offer, duration, recurrence, credits),
    [prices, offer, duration, recurrence, credits],
  );
};

export const getDisplayablePriceList = (
  prices: DetailsPrices,
  offer?: Partial<Offer>,
  duration?: number,
  recurrence?: Recurrence,
  credits?: number,
): DisplayablePrice[] => {
  const res = [];

  if (offer?.requireCallToSales) {
    if (offer.type === OfferType.CREDIT) {
      return [
        {
          label: 'YOUR_AMOUNT',
          extraCredits: credits,
        },
      ];
    }
    return [{ label: 'CUSTOM' }];
  }

  if (prices.eur) {
    res.push({
      unit: DisplayablePriceUnit.EUR,
      price: prices.eur,
      extraCredits: credits ? credits - prices.eur : undefined,
    });
  }

  if (prices.perParticipant) {
    if (duration && prices.perParticipantPerHour) {
      res.push({
        unit: DisplayablePriceUnit.PER_PARTICIPANT,
        price:
          prices.perParticipant +
          applyDurationToPrice(prices.perParticipantPerHour, duration),
      });
    } else {
      res.push({
        unit: DisplayablePriceUnit.PER_PARTICIPANT,
        price: prices.perParticipant,
      });
      if (prices.perParticipantPerHour) {
        res.push({
          unit: DisplayablePriceUnit.PER_PARTICIPANT_PER_HOUR,
          price: prices.perParticipantPerHour,
        });
      }
    }
  } else if (prices.perParticipantPerHour) {
    if (duration) {
      res.push({
        unit: DisplayablePriceUnit.PER_PARTICIPANT,
        price: applyDurationToPrice(prices.perParticipantPerHour, duration),
      });
    } else {
      res.push({
        unit: DisplayablePriceUnit.PER_PARTICIPANT_PER_HOUR,
        price: prices.perParticipantPerHour,
      });
    }
  }

  if (prices.monthly || prices.yearly) {
    const rec = recurrence
      ? (recurrence as string as DisplayablePriceUnit)
      : prices.monthly
      ? DisplayablePriceUnit.MONTHLY
      : DisplayablePriceUnit.YEARLY;

    res.push({
      unit: rec,
      price:
        rec === DisplayablePriceUnit.MONTHLY ? prices.monthly : prices.yearly,
    });
  }

  if (!res.length) {
    if (offer?.type === OfferType.OPTION || offer?.type === OfferType.SUPPORT) {
      return [{ label: 'INCLUDED' }];
    }
    return [{ label: 'FREE' }];
  }

  return res;
};
